import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import {
  Bell, Trash2, CheckCheck, ShoppingCart, AlertTriangle,
  XCircle, Wrench, Gavel, FileText, Filter, ChevronDown,
} from "lucide-react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "../../../assets/css/admin/notifications/AdminNotifications.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ── Type meta ─────────────────────────────────────────────────────────────────
const TYPE_META = {
  order:    { label: "Order",    color: "#3B6D11", bg: "rgba(92,107,71,0.12)",    Icon: ShoppingCart  },
  stock:    { label: "Low Stock",color: "#854F0B", bg: "rgba(186,117,23,0.12)",   Icon: AlertTriangle },
  critical: { label: "Critical", color: "#A32D2D", bg: "rgba(226,75,74,0.12)",    Icon: XCircle       },
  custom:   { label: "Custom",   color: "#3C3489", bg: "rgba(83,74,183,0.12)",    Icon: Wrench        },
  bid:      { label: "Bid",      color: "#0F6E56", bg: "rgba(29,158,117,0.12)",   Icon: Gavel         },
  quote:    { label: "Quote",    color: "#6B4C3B", bg: "rgba(107,76,59,0.12)",    Icon: FileText      },
};

const getTypeMeta = (type) =>
  TYPE_META[type] || { label: type, color: "#888", bg: "rgba(136,136,136,0.1)", Icon: Bell };

// ── Component ─────────────────────────────────────────────────────────────────
const AdminNotifications = () => {
  const { baseUrl, authToken, fetchAdminNotificationCount } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter]               = useState("all");
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const [menuPos, setMenuPos]             = useState({ top: 0, left: 0 });
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const triggerRef = useRef(null);
  const menuRef    = useRef(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  // ── Fetch notifications ──────────────────────────────────────────────────────
  const fetchNotifications = useCallback(() => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/notification/getNotification`, { headers })
      .then((res) => {
        setNotifications(res.data.notifications || []);
        setError(null);
      })
      .catch(() => {
        setError("Failed to load notifications");
        setNotifications([]);
      })
      .finally(() => setLoading(false));
  }, [baseUrl, authToken]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // ── Derived values ───────────────────────────────────────────────────────────
  // DB mein status field hai — "unread"/"read"
  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  // ── Mark single as read ──────────────────────────────────────────────────────
  const markAsRead = (id) => {
    const notif = notifications.find((n) => n.id === id);
    if (!notif || notif.status === "read") return;

    axios
      .post(`${baseUrl}/admin/notification/markAsRead/${id}`, {}, { headers })
      .then(() => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, status: "read" } : n))
        );
        fetchAdminNotificationCount();
      })
      .catch(() => Swal.fire("Error", "Could not mark as read", "error"));
  };

  // ── Mark ALL as read ─────────────────────────────────────────────────────────
  const markAllAsRead = () => {
    if (unreadCount === 0) return;

    // unread notifications ki IDs nikalo
    const unreadIds = notifications
      .filter((n) => n.status === "unread")
      .map((n) => n.id);

    Promise.all(
      unreadIds.map((id) =>
        axios.post(`${baseUrl}/admin/notification/markAsRead/${id}`, {}, { headers })
      )
    )
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
        fetchAdminNotificationCount();
        Swal.fire({ icon: "success", title: "Done!", text: "All marked as read", timer: 1500, showConfirmButton: false });
      })
      .catch(() => Swal.fire("Error", "Could not mark all as read", "error"));
  };

  // ── Delete single notification ───────────────────────────────────────────────
  const deleteNotification = (id) => {
    Swal.fire({
      title: "Delete Notification?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6B4C3B",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios
        .delete(`${baseUrl}/admin/notification/deleteNotification/${id}`, { headers })
        .then(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        })
        .catch(() => Swal.fire("Error", "Could not delete notification", "error"));
    });
  };

  // ── Clear ALL ────────────────────────────────────────────────────────────────
  const clearAll = () => {
    if (notifications.length === 0) return;

    Swal.fire({
      title: "Clear All Notifications?",
      text: "All notifications will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6B4C3B",
      confirmButtonText: "Clear All",
    }).then((result) => {
      if (!result.isConfirmed) return;

      // sab individually delete karo (admin ka clearAll route nahi hai)
      const allIds = notifications.map((n) => n.id);
      Promise.all(
        allIds.map((id) =>
          axios.delete(`${baseUrl}/admin/notification/deleteNotification/${id}`, { headers })
        )
      )
        .then(() => {
          setNotifications([]);
          Swal.fire({ icon: "success", title: "Cleared!", timer: 1500, showConfirmButton: false });
        })
        .catch(() => Swal.fire("Error", "Could not clear notifications", "error"));
    });
  };

  // ── Filter config ────────────────────────────────────────────────────────────
  const filters = [
    { key: "all",      label: "All",          count: notifications.length },
    { key: "unread",   label: "Unread",        count: unreadCount },
    { key: "order",    label: "Orders",        count: notifications.filter((n) => n.type === "order").length },
    { key: "stock",    label: "Low Stock",     count: notifications.filter((n) => n.type === "stock").length },
    { key: "critical", label: "Critical",      count: notifications.filter((n) => n.type === "critical").length },
    { key: "custom",   label: "Custom Orders", count: notifications.filter((n) => n.type === "custom").length },
    { key: "bid",      label: "Bids",          count: notifications.filter((n) => n.type === "bid").length },
    { key: "quote",    label: "Quotes",        count: notifications.filter((n) => n.type === "quote").length },
  ];

  const activeFilter = filters.find((f) => f.key === filter);

  const filtered = notifications.filter((n) => {
    if (filter === "all")    return true;
    if (filter === "unread") return n.status === "unread";
    return n.type === filter;
  });

  // ── Dropdown position ────────────────────────────────────────────────────────
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect      = triggerRef.current.getBoundingClientRect();
      const menuWidth = 200;
      const vw        = window.innerWidth;
      let left = rect.right - menuWidth;
      if (left < 8) left = rect.left;
      if (left + menuWidth > vw - 8) left = vw - menuWidth - 8;
      setMenuPos({ top: rect.bottom + 8, left });
    }
    setDropdownOpen((p) => !p);
  };

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const close = () => setDropdownOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="admin-ntf-page">

      {/* HEADER */}
      <div className="admin-ntf-header">
        <div className="admin-ntf-header-left">
          <div className="admin-ntf-header-icon">
            <Bell size={20} strokeWidth={1.8} />
            {unreadCount > 0 && <span className="admin-ntf-badge-dot">{unreadCount}</span>}
          </div>
          <div>
            <h1 className="admin-ntf-title">Notifications</h1>
            <p className="admin-ntf-subtitle">
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up!"}
            </p>
          </div>
        </div>

        <div className="admin-ntf-header-right">
          <button
            ref={triggerRef}
            className={`admin-ntf-dd-btn ${dropdownOpen ? "admin-ntf-dd-btn-open" : ""}`}
            onClick={openDropdown}
          >
            <Filter size={13} strokeWidth={2.5} />
            <span>{activeFilter?.label}</span>
            {activeFilter?.count > 0 && <span className="admin-ntf-dd-chip">{activeFilter.count}</span>}
            <ChevronDown size={13} strokeWidth={2.5} className="admin-ntf-dd-chevron" />
          </button>

          <button
            className="admin-ntf-ctrl-btn"
            onClick={markAllAsRead}
            title="Mark all as read"
            disabled={unreadCount === 0}
          >
            <CheckCheck size={15} strokeWidth={2} />
            <span className="admin-ntf-ctrl-label">Read all</span>
          </button>

          <button
            className="admin-ntf-ctrl-btn admin-ntf-ctrl-danger"
            onClick={clearAll}
            title="Clear all"
            disabled={notifications.length === 0}
          >
            <Trash2 size={15} strokeWidth={2} />
            <span className="admin-ntf-ctrl-label">Clear</span>
          </button>
        </div>
      </div>

      {/* DROPDOWN */}
      {dropdownOpen && (
        <>
          <div className="admin-ntf-backdrop" onClick={() => setDropdownOpen(false)} />
          <div ref={menuRef} className="admin-ntf-dd-menu" style={{ top: menuPos.top, left: menuPos.left }}>
            <p className="admin-ntf-dd-menu-title">Filter by</p>
            {filters.map((f) => (
              <button
                key={f.key}
                className={`admin-ntf-dd-item ${filter === f.key ? "admin-ntf-dd-item-on" : ""}`}
                onClick={() => { setFilter(f.key); setDropdownOpen(false); }}
              >
                <span>{f.label}</span>
                <span className="admin-ntf-dd-item-count">{f.count}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* CONTENT */}
      {loading ? (
        <div className="admin-ntf-empty">
          <Bell size={44} strokeWidth={1.2} color="#D67E43" style={{ opacity: 0.4 }} />
          <p>Loading notifications...</p>
        </div>
      ) : error ? (
        <div className="admin-ntf-empty">
          <Bell size={44} strokeWidth={1.2} color="#d33" style={{ opacity: 0.4 }} />
          <h3>Oops!</h3>
          <p>{error}</p>
          <button onClick={fetchNotifications} className="admin-ntf-retry-btn">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="admin-ntf-empty">
          <Bell size={44} strokeWidth={1.2} color="#D67E43" style={{ opacity: 0.4 }} />
          <h3>No Notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="admin-ntf-list">
          {filtered.map((n) => {
            const meta     = getTypeMeta(n.type);
            const TypeIcon = meta.Icon;
            return (
              <div
                key={n.id}
                className={`admin-ntf-item ${n.status === "read" ? "admin-ntf-item-read" : "admin-ntf-item-unread"}`}
                onClick={() => markAsRead(n.id)}
              >
                <div className="admin-ntf-item-icon" style={{ background: meta.bg, color: meta.color }}>
                  <TypeIcon size={18} strokeWidth={1.8} />
                </div>

                <div className="admin-ntf-item-body">
                  <div className="admin-ntf-item-top">
                    <span className="admin-ntf-item-title">{n.title}</span>
                    <span className="admin-ntf-type-badge" style={{ color: meta.color, background: meta.bg }}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="admin-ntf-item-msg">{n.description}</p>
                  <span className="admin-ntf-item-time">
                    {new Date(n.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="admin-ntf-item-actions">
                  {n.status === "unread" && <span className="admin-ntf-unread-dot" />}
                  <button
                    className="admin-ntf-delete-btn"
                    onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                    title="Delete"
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;